import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/base/TopicCard';
import useSeo from '@/hooks/useSeo';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/site';

const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const LIBRARY_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '看料资源库 - 在线看料全集完整版',
    url: `${SITE_URL}/library`,
    description: '看料社资源库收录全部在线看料专题与系列合集，支持关键词搜索与主题分类筛选。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '资源库', item: `${SITE_URL}/library` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '看料社专题合集列表',
    itemListElement: topics.map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.title,
      url: `${SITE_URL}/topic/${t.id}`,
    })),
  },
];

const sortOptions = [
  { id: 'latest', label: '最近更新' },
  { id: 'hot', label: '热度最高' },
  { id: 'count', label: '整理最多' },
];

const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? '专题';

export default function Library() {
  const [params, setParams] = useSearchParams();
  const [keyword, setKeyword] = useState(params.get('q') ?? '');

  const activeCat = params.get('cat') ?? 'all';
  const activeSort = params.get('sort') ?? 'latest';

  useSeo({
    title: '看料资源库 - 在线看料全集完整版与系列专题合集',
    description:
      '看料社资源库收录全部在线看料专题与系列合集，支持关键词搜索、主题分类筛选与热度排序，在线看料完整版想看哪部点哪部。',
    keywords: '在线看料资源库,在线看料全集,在线看料完整版,在线看料系列,在线看料专题,看料合集',
    type: 'website',
    jsonLd: LIBRARY_JSONLD,
  });

  const filtered = useMemo(() => {
    const q = (params.get('q') ?? '').trim().toLowerCase();
    let list = topics.filter((t) => {
      const matchCat = activeCat === 'all' || t.categoryId === activeCat;
      const matchQ =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCat && matchQ;
    });
    if (activeSort === 'hot') list = [...list].sort((a, b) => b.hotScore - a.hotScore);
    else if (activeSort === 'count') list = [...list].sort((a, b) => b.articleCount - a.articleCount);
    else list = [...list].sort((a, b) => (a.updateTime < b.updateTime ? 1 : -1));
    return list;
  }, [params, activeCat, activeSort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParam('q', keyword.trim());
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '资源库' }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950">
              在线看料资源库
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              这里收录了看料社整理的全部专题与系列合集。你可以按主题分类浏览，也可以用关键词直接搜索，找到想看的那一期。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="flex flex-col gap-5 mb-8">
              <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="输入关键词，例如：名场面、翻车、宣发"
                    className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </form>

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateParam('cat', 'all')}
                    className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      activeCat === 'all'
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                    }`}
                  >
                    全部分类
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => updateParam('cat', c.id)}
                      className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        activeCat === c.id
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-foreground-500">排序</span>
                  <div className="flex items-center gap-1 p-1 rounded-full bg-background-200">
                    {sortOptions.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => updateParam('sort', o.id)}
                        className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                          activeSort === o.id
                            ? 'bg-background-50 text-foreground-950'
                            : 'text-foreground-600 hover:text-foreground-900'
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-foreground-500 mb-6">
              共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 个专题
            </p>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((t) => (
                  <TopicCard key={t.id} topic={t} categoryName={catName(t.categoryId)} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center rounded-lg border border-dashed border-background-300">
                <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="text-foreground-900 font-medium">没有找到匹配的专题</p>
                <p className="mt-2 text-sm text-foreground-500">试试更换关键词或切换分类</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setParams(new URLSearchParams());
                  }}
                  className="mt-5 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}